"use client";

import { useState } from "react";
import { CircleGauge, Sparkles, Wallet } from "lucide-react";
import { GlobalShell } from "@/components/shell/global-shell";
import { KpiCard } from "@/components/primitives/kpi-card";
import { AttentionCard } from "@/components/primitives/attention-card";
import { AiActionPanel } from "@/components/primitives/ai-action-panel";
import { GuestIdentityChip } from "@/components/primitives/guest-identity-header";
import { Drawer } from "@/components/primitives/drawer";
import { GuestIdentityHeader } from "@/components/primitives/guest-identity-header";
import { MOCK_AI_ACTIONS, MOCK_GUEST } from "@/lib/mock-data";

/**
 * Minimal real shell layout: Global Shell + Context Bar composed with a demo
 * "Today" operating view built from the Phase 0 primitives. This is a real
 * page (not a gallery fragment) using mock data only — there is no backend
 * yet for the orchestrator's API phase to wire up later.
 */
export default function TodayPage() {
  const [activeNav, setActiveNav] = useState("today");
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <GlobalShell
      activeNavId={activeNav}
      onNavigate={setActiveNav}
      contextBar={{ service: "Dinner service · 18:00–00:00", dateLabel: "Tuesday, 22 September 2026", operatingState: "open" }}
    >
      <div>
        <h1 className="font-display text-[28px] font-medium text-text-primary sm:text-[32px]">
          Good afternoon. Dinner is shaping up well.
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-text-secondary">
          A live operating view of covers, guest activity, revenue opportunities and items that need attention before service.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard
          label="Booked covers"
          value="84"
          delta="14% vs last Tuesday"
          deltaDirection="up"
          icon={<CircleGauge size={16} />}
        />
        <KpiCard
          label="Expected occupancy"
          value="91%"
          delta="Strong dinner service"
          deltaDirection="up"
          icon={<CircleGauge size={16} />}
        />
        <KpiCard
          label="Revenue at risk"
          value="SAR 2.4K"
          delta="12 open covers at 8:30"
          deltaDirection="down"
          icon={<Wallet size={16} />}
        />
        <KpiCard
          label="Guest value today"
          value="SAR 17.8K"
          delta="POS-estimated based on history"
          deltaDirection="flat"
          icon={<Wallet size={16} />}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-3">
          <AttentionCard
            title="12 covers still open at 8:30 PM"
            description="High-intent WhatsApp segment identified — filling this closes tonight's revenue gap."
            severity="warning"
            primaryAction={{ label: "Review waitlist" }}
            secondaryAction={{ label: "Dismiss" }}
          />
          <div className="rounded-card border border-border-subtle bg-surface-primary p-4 shadow-[var(--shadow-subtle)]">
            <p className="mb-3 text-sm font-bold text-text-primary">Next arrivals</p>
            <div className="flex flex-wrap gap-2">
              <GuestIdentityChip guest={MOCK_GUEST} onOpenProfile={() => setDrawerOpen(true)} />
            </div>
          </div>
        </div>

        <AiActionPanel
          heading="AI Revenue Copilot"
          actions={MOCK_AI_ACTIONS.slice(0, 3)}
          footer={
            <div className="flex items-center gap-2 text-xs text-white/70">
              <Sparkles aria-hidden="true" size={13} />
              Full state matrix demo available on /design-system
            </div>
          }
        />
      </div>

      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="Guest profile"
        description="Placeholder workspace — reservation/guest-profile content is out of Phase 0 scope."
        footer={
          <>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="rounded-control border border-border-strong px-3 py-2 text-xs font-bold text-text-primary hover:bg-surface-secondary"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="rounded-control bg-brand-primary px-3 py-2 text-xs font-bold text-brand-on hover:bg-brand-hover"
            >
              Open full profile
            </button>
          </>
        }
      >
        <GuestIdentityHeader guest={MOCK_GUEST} />
        <p className="mt-4 text-xs text-text-secondary">
          This is the generic Drawer primitive shown with guest-header-shaped placeholder content, per the Phase 0 brief — actual
          reservation/guest-profile workflows are a later phase.
        </p>
      </Drawer>
    </GlobalShell>
  );
}
